/**
 * NOTE (kyle): this file uses some `date-fns` to parse and display python ISO
 * dates, but for the most part JS Date provides a good API for handling dates.
 * In the future we should be able to remove `date-fns` entirely in favor of
 * the ES Temporal API.
 */

import type {TranslationFunction} from 'src/types/language-support';
import moment from 'moment';
import parseISO from 'date-fns/parseISO';
import formatISO from 'date-fns/formatISO';
import startOfDay from 'date-fns/startOfDay';

import isValid from 'date-fns/isValid';
import isBefore from 'date-fns/isBefore';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';

import momentTZ from 'moment-timezone';
import {localeFormats, type LocaleKey} from 'src/constants/language-support';
import {captureSentryMessage} from 'src/utils/sentry';
import logger from 'src/utils/logger';


export const DEFAULT_TIMEZONE = 'America/Los_Angeles';

// Regex to match valid ISO 8601 date formats:
const ISO_DATE_REGEX =
  /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;

export function compareTimestamps(
  timestampA: string,
  timestampB: string,
): number {
  return timestampA === timestampB ? 0 : timestampA < timestampB ? 1 : -1;
}

export function displayNumericDate(
  date?: string | Date,
  locale?: string,
): string {
  const formatter = new Intl.DateTimeFormat(locale || 'en-US');
  return formatter.format(wrangleMoment(date));
}

// ex Dec 9
export function displayShortDate(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language?: string,
): string {
  const shortDateFormatter = new Intl.DateTimeFormat(language || undefined, {
    month: 'short',
    day: 'numeric',
  });
  return shortDateFormatter.format(wrangleMoment(date));
}

//Formats a date in the given timezone
export function formatDateInTimezone(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  timezone?: string,
  formatOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  },
  locale?: string,
): string {
  const options: Intl.DateTimeFormatOptions = timezone
    ? {...formatOptions, timeZone: timezone}
    : formatOptions;
  const formatter = new Intl.DateTimeFormat(locale || 'en-US', options);
  return formatter.format(wrangleMoment(date));
}

// ex Dec 9, 1995
export function displayDate(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  locale: string = 'en',
): string {
  const formatter = new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  return formatter.format(wrangleMoment(date));
}

// ex Oct 22, 2021, 6:24 AM
export function displayDateTime(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  locale?: string,
): string {
  const formatter = new Intl.DateTimeFormat(locale || 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return formatter.format(wrangleMoment(date));
}

export function displayFullDateTime(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  locale?: string,
): string {
  const fullDateTimeFormatter = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
  return fullDateTimeFormatter.format(wrangleMoment(date));
}

export function displayTime(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
  language?: string,
  // @ts-expect-error TS2304
  formatOptions?: Intl$DateTimeFormatOptions,
): string {
  const timeFormatter = new Intl.DateTimeFormat(
    language || undefined,
    formatOptions || {
      hour: 'numeric',
      minute: '2-digit',
    },
  );
  return timeFormatter.format(wrangleMoment(date));
}

export function displayTerseDateTime(
  timestamp: string,
  locale?: string,
): string {
  const date = parseISO(timestamp);
  const today = startOfDay(new Date());
  // If the timestamp is more than a day old,
  // display its date. Otherwise display its time.
  if (date < today) {
    return displayDate(date, locale);
  } else {
    return displayTime(date, locale);
  }
}

// @ts-expect-error TS2304
export function formatIsoDate(date?: string | moment$Moment | Date): string {
  return formatISO(wrangleMoment(date), {
    representation: 'date',
  });
}

// @ts-expect-error TS2304
export function wrangleMoment(date?: string | Date | moment$Moment): Date {
  let parsedDate;
  if (date instanceof Date) {
    parsedDate = date;
  } else if (!date) {
    parsedDate = new Date();
  } else {
    try {
      // @ts-expect-error - TS2339 - Property 'toDate' does not exist on type '{}'.
      parsedDate = date instanceof moment ? date.toDate() : parseISO(date);
    } catch (error: any) {
      captureSentryMessage(`Error parsing date: ${error.message}`, {
        level: 'error',
        tags: {product: 'scheduler', inputDate: date},
      });
      logger.warn('Error parsing date', {date, error});
      return new Date();
    }
  }

  // Check if the parsedDate is valid
  if (isNaN(parsedDate.getTime())) {
    captureSentryMessage(`Invalid date: ${parsedDate.getTime()}`, {
      level: 'error',
      tags: {product: 'scheduler', parsedDate},
    });
    logger.warn('Invalid Date', date);
    return new Date();
  }

  return parsedDate;
}

export function getISODate(date?: Date): string {
  return formatIsoDate(date);
}

export const getUTCDate = (date: string | Date): string =>
  moment(date).utc().toISOString();

export const getUTCDateWithTimezone = (
  date: string,
  timezone: string,
): string => momentTZ.tz(date, timezone).format('MM/DD/YYYY hh:mm A');

export function formatDaysAgo(
  value: string,
  locales: string = 'en-US',
  t: TranslationFunction | null = null,
): string {
  const date = wrangleMoment(value);

  const diff = (date.getTime() - Date.now()) / (1000 * 3600 * 24);
  const formatter = new Intl.RelativeTimeFormat(locales, {numeric: 'auto'});
  const absDiff = Math.abs(diff);

  if (absDiff >= 365) {
    return formatter.format(Math.round(diff / 365), 'year');
  }
  if (absDiff >= 30) {
    return formatter.format(Math.round(diff / 30), 'month');
  }
  if (absDiff >= 7) {
    return formatter.format(Math.round(diff / 7), 'week');
  }
  if (absDiff >= 1) {
    return formatter.format(Math.round(diff), 'day');
  }
  //Time formatting
  let timeDiff = diff * 24;
  let truncatedTimeDiff = Math.trunc(timeDiff);
  let absoluteTimeDiff = Math.abs(timeDiff);
  if (absoluteTimeDiff > 1) {
    return formatter.format(truncatedTimeDiff, 'hour');
  }
  timeDiff = timeDiff * 60;
  truncatedTimeDiff = Math.trunc(timeDiff);
  absoluteTimeDiff = Math.abs(timeDiff);
  if (absoluteTimeDiff > 1) {
    return formatter.format(truncatedTimeDiff, 'minute');
  }
  return t ? t('just_now', 'just now') : 'just now';
}

export function formateTimeAgo(value: string, locale: string = 'en'): string {
  const date = wrangleMoment(value);
  return moment(date).locale(locale).fromNow();
}

const getDateFnsLocale = (locale: string) => {
  const {locale: dateFnsLocale} =
    localeFormats[locale as LocaleKey] || localeFormats.en;
  return dateFnsLocale;
};

type FormatLabeledRelativeTimeOptions = {
  timestamp: string | null | undefined;
  labelKey: string;
  defaultLabel: string;
  t: TranslationFunction;
  locale?: string;
};

export function formatLabeledRelativeTime({
  timestamp,
  labelKey,
  defaultLabel,
  t,
  locale = 'en',
}: FormatLabeledRelativeTimeOptions): string | null {
  if (timestamp == null) {
    return null;
  }

  let date: Date;
  try {
    const parsed = parseISO(timestamp);
    if (!isValid(parsed)) {
      return null;
    }
    date = parsed;
  } catch {
    return null;
  }

  const relative = formatDistanceToNow(date, {
    addSuffix: true,
    locale: getDateFnsLocale(locale),
  });
  return `${t(labelKey, defaultLabel)} ${relative}`;
}

export const compareISODates = (
  dateStr1: string,
  dateStr2: string,
): number | null | undefined => {
  const date1 = new Date(dateStr1);
  const date2 = new Date(dateStr2);
  // @ts-expect-error - TS2345 - Argument of type 'Date' is not assignable to parameter of type 'number'. | TS2345 - Argument of type 'Date' is not assignable to parameter of type 'number'. | TS2362 - The left-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type. | TS2363 - The right-hand side of an arithmetic operation must be of type 'any', 'number', 'bigint' or an enum type.
  return isNaN(date1) || isNaN(date2) ? null : date1 - date2;
};

// This function takes a Date object as input and returns a formatted date string.
// Format the Date object as "YYYY-MM-DD" , this date format is accepted by HTML Date Picker for min max
export const formatDate = (date: Date): string => {
  return date.toISOString().split('T')[0];
};

// This function takes a date string and subtracts given numberOfDays from that Date and returns a formatted date string.
export const getSubtractedDays = (
  date: string,
  numberOfDays: number,
): string => {
  return moment(date).subtract(numberOfDays, 'd').format('YYYY-MM-DD');
};

export const getUTCStartOfDay = (
  date: string,
  timezone: string = momentTZ.tz.guess(),
): string => momentTZ.tz(date, timezone).startOf('d').utc().toISOString();

export const getUTCEndOfDay = (
  date: string,
  timezone: string = momentTZ.tz.guess(),
): string => momentTZ.tz(date, timezone).endOf('d').utc().toISOString();

const dateTime12HrsFormatter = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

export function displayDate12HrsTime(
  // @ts-expect-error TS2304
  date?: string | Date | moment$Moment,
): string {
  return dateTime12HrsFormatter.format(wrangleMoment(date));
}

export function formatMonthDay(
  monthDay: string,
  locale: string = 'en',
): string {
  const date = moment(monthDay, 'MM-DD').locale(locale);
  return date.format('D MMMM');
}

export function convertToMonthDay(dateString: string, locale?: string): string {
  const date = moment(dateString);
  return date.locale(locale || 'en').format('DD MMM');
}

export const getTimein12HrsFormat = (time: string, locale?: string): string => {
  const format = 'HH:mm:ss';
  const momentTime = moment(time, format).locale(locale || 'en');
  return momentTime.format('h:mm A');
};

export const checkTime1LessThanTime2 = (
  time1: string,
  time2: string,
): boolean => {
  const format = 'HH:mm:ss';
  const t1 = moment(time1, format);
  const t2 = moment(time2, format);
  return t1.isBefore(t2);
};

export const formatDateToUTCMonthYear = (
  dateString: string,
  locale: string = 'en',
): string => {
  return moment.utc(dateString).locale(locale).format('MMM YY');
};

/*note(vish): This function compares only the date portion (year, month, day), ignoring time components.
 * It uses date-fns for consistent date comparison across timezones. */
export const isDateLessThanTodaysDate = (date: string): boolean => {
  const parsedDate = parseISO(date);
  if (!isValid(parsedDate)) {
    return false;
  }
  const today = startOfDay(new Date());
  return isBefore(parsedDate, today);
};

export const minutesToHoursMinutes = (minutes: number): string => {
  const min = Math.abs(minutes);
  if (minutes < 0) {
    return '0m';
  }

  const hours = Math.floor(min / 60);
  const remainingMinutes = min % 60;

  if (hours === 0) {
    return `${remainingMinutes}m`;
  }
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${remainingMinutes}m`;
};

export const formatDateWithLocale = (
  date: string,
  locale: string,
  format: string,
): string => {
  return moment(date).locale(locale).format(format);
};

export const isValidISODate = (
  dateString: string | null | undefined,
): boolean => {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  const isoDateRegex = ISO_DATE_REGEX;

  if (!isoDateRegex.test(dateString)) {
    return false;
  }
  try {
    const parsedDate = parseISO(dateString);
    return isValid(parsedDate);
  } catch (error) {
    return false;
  }
};
